import type {CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Canvas, Point} from '@postermywall/fabricjs-2';
import type {CropFrame} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {isCropImageCorner} from '@PosterWhiteboard/poster/poster-media-crop/poster-media-crop.types';
import {ItemBorderColor} from '@PosterWhiteboard/poster/poster-item-controls';
import {degreesToRadians} from '@Utils/math.util';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {CropSurface} from '@Components/poster-editor/components/crop-surface/crop-surface';
import {CustomScaledDimensionsInfoBox} from '@Components/poster-editor/components/custom-item-controls/components/custom-scaled-dimensions-info-box/custom-scaled-dimensions-info-box';
import type {FabricObjectPositionOrDimensionKeys} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CUSTOM_ITEM_OUTLINE_THICKNESS} from '@Components/poster-editor/components/custom-item-controls/components/custom-outline-boxes/custom-outline-boxes.types';
import {getRotatedControlAnchor} from '@Components/poster-editor/components/custom-item-controls/custom-item-control-anchor.util';
import type {CropBoxStyle, CropDragTarget, CropHandleSpec, CropHandlesOverlayProps} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay.types';
import {getAngleAround, getDragCursor, getFrameCentre, getHandleGrabReach, isImageCornerCrowdedByWindow, isPressWithinHandleReach, snapCropRotation} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay.library';
import {CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE, CustomControlCursor} from '@Components/poster-editor/components/custom-item-controls/custom-item-controls.types';
import {CUSTOM_ROTATE_CONTROL_MARGIN} from '@Components/poster-editor/components/custom-rotation-control/custom-rotation-control.types';
import {
  CROP_CORNER_BRACKET_PATH,
  CROP_CORNER_BRACKET_VIEW_BOX,
  CROP_HANDLE_SPECS,
  CROP_IMAGE_CORNER_CONTROL_ID_PREFIX,
  CROP_ROTATION_CONTROL_ID,
  CROP_IMAGE_CORNER_RADIUS,
  CROP_IMAGE_CORNER_SIDE,
  CropDragKind,
} from '@Components/poster-editor/components/crop-handles-overlay/crop-handles-overlay.types';
import {useAppSelector} from '@/hooks';
import styles from './crop-handles-overlay.module.scss';

export function CropHandlesOverlay(props: CropHandlesOverlayProps): ReactElement | null {
  const frames = useAppSelector((state) => {
    return state.mediaCropMode.frames;
  });
  const aspectRatio = useAppSelector((state) => {
    return state.mediaCropMode.aspectRatio;
  });
  const posterScale = useAppSelector((state) => {
    return state.posterEditor?.posterObject?.scaling.scale;
  });
  const rotation = useAppSelector((state) => {
    return state.mediaCropMode.rotation;
  });
  const [dragTarget, setDragTarget] = useState<CropDragTarget | null>(null);

  /**
   * A drag listens on `window`, not the handle, so the pointer may leave the handle - and the canvas -
   * without the frame sticking at the last position it saw. The grab cursor is held for the whole
   * gesture and released on cleanup, so an unmount mid-drag cannot strand it.
   */
  useEffect(() => {
    if (!dragTarget) {
      return undefined;
    }

    const onPointerMove = (event: PointerEvent): void => {
      const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
      const mediaCrop = window.posterEditor?.whiteboard?.mediaCrop;
      if (!page || !mediaCrop || !(page.fabricCanvas instanceof Canvas)) {
        return;
      }
      const scenePoint = page.fabricCanvas.getScenePoint(event);
      if (dragTarget.kind === CropDragKind.ROTATE) {
        const currentFrames = window.PMW.redux.store.getState().mediaCropMode.frames;
        if (!currentFrames) {
          return;
        }
        const pointerAngle = getAngleAround(getFrameCentre(currentFrames.window), scenePoint);
        mediaCrop.rotatePictureFromHandle(snapCropRotation(dragTarget.startRotation + pointerAngle - dragTarget.startPointerAngle));
        return;
      }
      if (dragTarget.kind === CropDragKind.MOVE) {
        const {startPointerScene, startWindowCentreScene} = dragTarget;
        mediaCrop.moveWindowCentreTo(new Point(startWindowCentreScene.x + scenePoint.x - startPointerScene.x, startWindowCentreScene.y + scenePoint.y - startPointerScene.y));
        return;
      }
      if (dragTarget.kind === CropDragKind.IMAGE) {
        if (isCropImageCorner(dragTarget.spec.handle)) {
          mediaCrop.scaleImageFromCorner(dragTarget.spec.handle, scenePoint);
        }
        return;
      }
      mediaCrop.resizeCropFromHandle(dragTarget.spec.handle, scenePoint);
    };
    const onPointerUp = (): void => {
      setDragTarget(null);
    };

    document.body.style.cursor = getDragCursor(dragTarget);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    return (): void => {
      document.body.style.cursor = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
    };
  }, [dragTarget, props.pageHashedId]);

  if (!frames || !posterScale) {
    return null;
  }

  // One set of handles at a time: while the crop window is being resized the image keeps only its box,
  // and while the image is being scaled or spun the window keeps only its box.
  const isDraggingWindowHandle = dragTarget?.kind === CropDragKind.WINDOW || dragTarget?.kind === CropDragKind.MOVE;
  const isDraggingImageHandle = dragTarget?.kind === CropDragKind.IMAGE || dragTarget?.kind === CropDragKind.ROTATE;

  const beginDrag = (event: ReactPointerEvent | ReactMouseEvent<HTMLDivElement>, target: CropDragTarget): void => {
    // Keeps the press off the canvas, where it would start a crop-window drag underneath.
    event.preventDefault();
    event.stopPropagation();
    setDragTarget(target);
  };

  /**
   * A press on a window handle resizes when it sits within reach of the edge or corner the handle moves.
   * A finger-sized handle covers much of a small window, so a press deeper inside moves the window, as a
   * press on the bare window would.
   */
  const beginWindowHandleDrag = (event: ReactPointerEvent, spec: CropHandleSpec): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page || !(page.fabricCanvas instanceof Canvas)) {
      return;
    }
    const scenePoint = page.fabricCanvas.getScenePoint(event.nativeEvent);
    if (isPressWithinHandleReach({frame: frames.window, spec, scenePoint, reach: getHandleGrabReach(frames.window, posterScale)})) {
      beginDrag(event, {kind: CropDragKind.WINDOW, spec});
      return;
    }
    beginDrag(event, {kind: CropDragKind.MOVE, startPointerScene: scenePoint, startWindowCentreScene: getFrameCentre(frames.window)});
  };

  const getCornerSpecs = (): CropHandleSpec[] => {
    return CROP_HANDLE_SPECS.filter((spec) => {
      return spec.isCorner;
    });
  };

  /** An edge drag cannot hold a ratio, so a locked ratio leaves only the corners. */
  const getVisibleWindowSpecs = (): CropHandleSpec[] => {
    return aspectRatio === null ? CROP_HANDLE_SPECS : getCornerSpecs();
  };

  /**
   * The box is placed and sized exactly on the edge it marks — an unrotated box centred on the
   * object, rotated about its centre like the item selection outline. Its line is an outline, so it
   * paints just outside the edge without moving the box's children off it.
   */
  const getBoxStyle = (frame: CropFrame): CropBoxStyle => {
    const widthOnScreen = frame.width * posterScale;
    const heightOnScreen = frame.height * posterScale;
    const angleRadians = degreesToRadians(frame.angle);
    const centerX = frame.left * posterScale + (widthOnScreen / 2) * Math.cos(angleRadians) - (heightOnScreen / 2) * Math.sin(angleRadians);
    const centerY = frame.top * posterScale + (widthOnScreen / 2) * Math.sin(angleRadians) + (heightOnScreen / 2) * Math.cos(angleRadians);
    return {
      left: `${centerX - widthOnScreen / 2}px`,
      top: `${centerY - heightOnScreen / 2}px`,
      width: `${widthOnScreen}px`,
      height: `${heightOnScreen}px`,
      rotate: `${frame.angle}deg`,
      outlineColor: ItemBorderColor.STATIC_ITEM,
      outlineWidth: `${CUSTOM_ITEM_OUTLINE_THICKNESS}px`,
      '--crop-outline-width': `${CUSTOM_ITEM_OUTLINE_THICKNESS}px`,
    };
  };

  /**
   * Edge bars are positioned from their spec and centred by CSS; corner brackets are anchored by
   * their own class to sit flush inside the corner, so they take no position here.
   */
  const getWindowHandleStyle = (spec: CropHandleSpec): CSSProperties => {
    if (spec.isCorner) {
      return {cursor: spec.cursor};
    }
    return {
      cursor: spec.cursor,
      left: `${spec.fractionX * 100}%`,
      top: `${spec.fractionY * 100}%`,
    };
  };

  const getWindowHandleClasses = (spec: CropHandleSpec): string => {
    return [styles.handle, spec.isCorner ? styles.corner : styles.edge, styles[spec.handle]].join(' ');
  };

  /**
   * The rotation control and the anchor helper read the same shape the item controls get from
   * fabric, so the image box is presented as an unscaled, stroke-less object whose width and height
   * are already on-screen sizes.
   */
  const getImagePositionProperties = (): FabricObjectPositionOrDimensionKeys => {
    return {
      left: frames.image.left,
      top: frames.image.top,
      width: frames.image.width,
      height: frames.image.height,
      scaleX: 1,
      scaleY: 1,
      scaledWidth: frames.image.width,
      scaledHeight: frames.image.height,
      posterScale,
      angle: frames.image.angle,
      strokeWidth: 0,
      borderScaleFactor: 0,
    };
  };

  /**
   * The picture's rotation handle starts a drag that turns the picture about the window's centre by
   * exactly the pointer's travel around that centre, so the handle keeps riding under the pointer.
   */
  const beginRotationDrag = (event: ReactPointerEvent | ReactMouseEvent<HTMLDivElement>): void => {
    const page = window.posterEditor?.whiteboard?.pages.getPage(props.pageHashedId);
    if (!page || !(page.fabricCanvas instanceof Canvas)) {
      return;
    }
    const startPointerAngle = getAngleAround(getFrameCentre(frames.window), page.fabricCanvas.getScenePoint(event.nativeEvent));
    beginDrag(event, {kind: CropDragKind.ROTATE, startPointerAngle, startRotation: rotation});
  };

  /**
   * Sits above the picture's top edge and turns with it, where the item rotation handle would sit on
   * an item. It is the picture's, not the window's: the window never turns in crop mode.
   */
  const getRotationControl = (): ReactElement | null => {
    if (isDraggingWindowHandle) {
      return null;
    }
    const halfHeightOnScreen = (frames.image.height * posterScale) / 2;
    const position = getRotatedControlAnchor({
      positionProperties: getImagePositionProperties(),
      controlWidth: CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE,
      controlHeight: CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE,
      offsetOnItemRight: 0,
      offsetOnItemDown: -(halfHeightOnScreen + CUSTOM_ROTATE_CONTROL_MARGIN),
      controlAngleDegrees: frames.image.angle,
    });
    return (
      <BaseCustomItemControl
        icon="icon-rotate-ccw"
        controlId={CROP_ROTATION_CONTROL_ID}
        top={position.top}
        left={position.left}
        width={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        height={CUSTOM_ITEM_CONTROL_WITH_ICON_SIDE}
        angle={position.angle}
        borderRadius="50%"
        isVisible
        cursor={CustomControlCursor.CURSOR_CROSSHAIR}
        onPointerDown={beginRotationDrag}
      />
    );
  };

  /** Absent while the window is being dragged, and wherever the window's corner has come close enough to stack on it. */
  const getImageCornerControl = (spec: CropHandleSpec): ReactElement | null => {
    if (isDraggingWindowHandle || isImageCornerCrowdedByWindow(frames, spec, posterScale)) {
      return null;
    }
    const halfWidthOnScreen = (frames.image.width * posterScale) / 2;
    const halfHeightOnScreen = (frames.image.height * posterScale) / 2;
    const position = getRotatedControlAnchor({
      positionProperties: getImagePositionProperties(),
      controlWidth: CROP_IMAGE_CORNER_SIDE,
      controlHeight: CROP_IMAGE_CORNER_SIDE,
      offsetOnItemRight: (spec.fractionX - 0.5) * 2 * halfWidthOnScreen,
      offsetOnItemDown: (spec.fractionY - 0.5) * 2 * halfHeightOnScreen,
      controlAngleDegrees: frames.image.angle,
    });
    return (
      <BaseCustomItemControl
        key={spec.handle}
        controlId={`${CROP_IMAGE_CORNER_CONTROL_ID_PREFIX}${spec.handle}`}
        top={position.top}
        left={position.left}
        width={CROP_IMAGE_CORNER_SIDE}
        height={CROP_IMAGE_CORNER_SIDE}
        angle={position.angle}
        borderRadius={CROP_IMAGE_CORNER_RADIUS}
        isVisible
        cursor={spec.cursor}
        onPointerDown={(event): void => {
          beginDrag(event, {kind: CropDragKind.IMAGE, spec});
        }}
      />
    );
  };

  /** The bracket mark of a corner handle: a bordered white L, like an item handle's bordered white square. Edges draw their pill in CSS. */
  const getWindowHandleMark = (spec: CropHandleSpec): ReactElement | null => {
    if (!spec.isCorner) {
      return null;
    }
    return (
      <svg className={styles.bracket} viewBox={CROP_CORNER_BRACKET_VIEW_BOX} aria-hidden="true">
        <path className={styles.bracketBorder} d={CROP_CORNER_BRACKET_PATH} />
        <path className={styles.bracketBar} d={CROP_CORNER_BRACKET_PATH} />
      </svg>
    );
  };

  const getWindowHandles = (): ReactElement[] => {
    if (isDraggingImageHandle) {
      return [];
    }
    return getVisibleWindowSpecs().map((spec) => {
      return (
        <div
          key={spec.handle}
          role="presentation"
          className={getWindowHandleClasses(spec)}
          style={getWindowHandleStyle(spec)}
          onPointerDown={(event): void => {
            beginWindowHandleDrag(event, spec);
          }}
        >
          {getWindowHandleMark(spec)}
        </div>
      );
    });
  };

  return (
    <CropSurface>
      <div className={styles.box} style={getBoxStyle(frames.image)} />
      {getCornerSpecs().map(getImageCornerControl)}
      {getRotationControl()}
      <div className={`${styles.box} ${styles.window}`} style={getBoxStyle(frames.window)}>
        {getWindowHandles()}
      </div>
      <CustomScaledDimensionsInfoBox pageHashedId={props.pageHashedId} positionProperties={getImagePositionProperties()} />
    </CropSurface>
  );
}
